import React, { useCallback, useEffect, useRef, useState } from 'react'
import Container from '../../base/Container'
import Card from '../../controls/Card'
import { useParams } from 'react-router-dom';
import { useToast } from '../../controls/Toast/ToastService';
import { FaUserMd } from 'react-icons/fa';
import {
  MdOutlineAccessTime,
  MdOutlineCalendarMonth,
  MdOutlineLocationOn,
  MdOutlineFreeBreakfast,
  MdOutlinePowerSettingsNew,
  MdSkipNext,
  MdOutlineMeetingRoom,
  MdHourglassEmpty,
  MdVolumeUp,
  MdVolumeOff,
  MdCampaign,
  MdOutlineFileDownload,
  MdWarningAmber,
} from 'react-icons/md';
import { VscDebugDisconnect } from 'react-icons/vsc';
import { PiPlugsConnectedFill } from 'react-icons/pi';
import { getLoginInfo } from '../../../configurations/auth/getLoginInfo';
import * as opdCentersCrud from '../../../configurations/crud/opdCentersCrud'
import * as opdRegisteredCrud from '../../../configurations/crud/opdRegisteredCrud'
import LoadingContainer from '../../controls/LoadingContainer';
import StatusContainer from '../../controls/StatusContainer';
import { ETypeBg, GetETypeName } from '../../../configurations/enums/EType';
import { allowSleep, keepAwake } from '../../../configurations/native/screenTime'
import { useKeepAwake } from 'react-keep-awake';
import useGlobalLoader from '../../../configurations/useGlobalLoader';
import moment from 'moment';
import useLiveRoom from './liveconfig/useLiveRoom'
import useBell from './liveconfig/useBell'
import useSpeak, { buildAnnouncement } from './liveconfig/useSpeak'
import SoundGate from './SoundGate'

// voice language choice device par yaad rahegi (display screen roz wahi rehti hai)
const VOICE_KEY = 'display_voice_lang'
// auto announcement on/off bhi yaad rahegi
const AUTO_KEY = 'display_auto_announce'
const VOICE_OPTIONS = [
  { id: 'hi-IN', label: 'हिंदी' },
  { id: 'en-IN', label: 'English' },
]

// announcement style ek hi jagah se control ho
// numberStyle 'natural' -> "54" ko engine "fifty four" bolta hai (digits me nahi todta)
// repeatNumber -> number do baar, taaki bheed me saaf sunai de
const ANNOUNCE_OPTS = { numberStyle: 'natural', repeatNumber: true }

export default function Display() {
  const { start, end } = useGlobalLoader()
  const [loding, setLoding] = useState(false);
  const toast = useToast();
  const { centerid } = useParams()
  const [doctorStatus, setDoctorStatus] = useState(1);
  const loginInfo = getLoginInfo()
  const [centerData, setCenterData] = useState();
  const [currentData, setCurrentData] = useState();
  const [nextPatientData, setNextPatientData] = useState();
  const [today, setToday] = useState(new Date());

  const isNative = !!(window.Capacitor?.isNativePlatform?.())

  useKeepAwake()
  useEffect(() => {
    keepAwake()
    return () => {
      allowSleep()
    }
  }, [])

  // ===== sound =====
  // Display par koi tap nahi karta, isliye SoundGate zaroori hai:
  // screen lagane ke baad ek baar tap -> poore din bell bajti rahegi.
  const { ring, unlock, unlocked } = useBell({
    bell: '/assets/audio/mixkit-cartoon-door-melodic-bell-110.wav',
  })

  // ===== voice announcement =====
  // default: हिंदी (device par hi-IN voice na ho to English par khud fallback ho jata hai)
  const [voiceLang, setVoiceLang] = useState(() => {
    try { return localStorage.getItem(VOICE_KEY) || 'hi-IN' } catch (e) { return 'hi-IN' }
  })
  const isHindi = voiceLang === 'hi-IN'
  const { speak, primeSpeech, stop, openVoiceInstall, supported } = useSpeak({ lang: voiceLang, rate: 0.9 })

  // Device par hi-IN voice data na ho to Devanagari text English engine ko chala jata hai
  // aur wo akshar-akshar padhta hai. Us case me English me bolo.
  const canHindi = isHindi && supported
  const hindiMissing = isHindi && !supported

  // kaunsa naam pehle hi bol chuke hain (60 sec wale sync par repeat na ho)
  const spokenRef = useRef({ cabin: null, next: null })

  // ===== auto announcement on/off =====
  // Default ON: patient badalte hi khud bolegi. OFF: sirf manual button / Reception ka "40".
  const [autoAnnounce, setAutoAnnounce] = useState(() => {
    try { return localStorage.getItem(AUTO_KEY) !== '0' } catch (e) { return true }   // sirf '0' hone par OFF
  })

  const toggleAutoAnnounce = () => {
    setAutoAnnounce(prev => {
      const next = !prev
      try { localStorage.setItem(AUTO_KEY, next ? '1' : '0') } catch (e) { /* ignore */ }
      if (next) primeSpeech()
      else stop()   // chalti announcement turant band
      return next
    })
  }

  // ek hi tap se bell + voice dono unlock
  const unlockAll = () => {
    unlock()
    primeSpeech()
  }

  const changeVoiceLang = (id) => {
    stop()                    // purani queue saaf, warna nayi awaaz peeche lagti hai
    setVoiceLang(id)
    try { localStorage.setItem(VOICE_KEY, id) } catch (e) { /* ignore */ }
    primeSpeech()
  }

  // language badalne par ek sample line -> staff ko turant pata chale kaunsi awaaz hai
  const langSampleRef = useRef(voiceLang)
  useEffect(() => {
    if (langSampleRef.current === voiceLang) return
    langSampleRef.current = voiceLang
    if (!unlocked) return
    const t = setTimeout(() => {
      speak(canHindi ? 'हिंदी आवाज़ चालू है।' : 'English voice is on.', { dedupeMs: 0 })
    }, 200)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [voiceLang, unlocked, canHindi])

  // announceAgain live message ("40") se bhi chalega, isliye latest values ref me rakhi hain
  // (warna useLiveRoom ka purana closure stale data bolwa dega)
  const liveRef = useRef({ data: null, canHindi: false, unlocked: false, speak, stop })
  useEffect(() => {
    liveRef.current = { data: currentData, canHindi, unlocked, speak, stop }
  })

  // current patient ko dobara pukarna (manual button ya Reception ka "40")
  const announceAgain = useCallback(() => {
    const { data, canHindi: hi, unlocked: ok, speak: say, stop: halt } = liveRef.current
    if (!ok || !data?.patientName) return
    halt()  // queue clear -> line poori aur saaf bolegi
    say(buildAnnouncement(data, 'cabin', { hindi: hi, ...ANNOUNCE_OPTS }), {
      interrupt: true,
      dedupeMs: 0,   // wahi text hai, dedupe skip karna zaroori hai
    })
  }, [])

  // ===== live room =====
  const handleMessage = (message) => {
    switch (message) {
      case "1": { GetCenterDetails(); break; }
      case "2":
      case "3":
      case "4": { GetPatientInTheCabin(); GetPatientWhoNext(); break; }
      case "5": { ring('bell'); break; }
      // 40 = Reception ne "dobara pukaro" bheja -> bell ke baad naam repeat
      case "40": {announceAgain(); break; }
      default: break;
    }
  }

  const refreshAll = () => {
    GetCenterDetails()
    GetPatientInTheCabin()
    GetPatientWhoNext()
  }

  const { isConnected, reconnect } = useLiveRoom({
    user: loginInfo.userName + "_" + centerid + "_Display",
    room: loginInfo.userName + "_" + centerid,
    onMessage: handleMessage,
    onRejoin: refreshAll,
  })

  const reConnect = async () => {
    await reconnect()
    unlockAll()
  }

  useEffect(() => {
    GetCenterDetails()
    GetPatientInTheCabin()
    GetPatientWhoNext()

    let myInterval = setInterval(() => {
      setToday(new Date())
    }, 1000);

    // safety net: har 60 sec par data phir se sync (agar koi message miss ho jaye)
    let syncInterval = setInterval(() => {
      GetPatientInTheCabin()
      GetPatientWhoNext()
    }, 60000);

    return () => {
      clearInterval(myInterval)
      clearInterval(syncInterval)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== announce: patient in the cabin =====
  // patient badalte hi (SignalR "2"/"3"/"4" ya 60 sec sync) ye effect chalta hai.
  // 1.5 sec delay -> pehle bell (message "5") baj jaye, phir naam.
  // interrupt nahi dete: hook ab default queue (ADD) karta hai, isse line beech-shabd me katti nahi.
  useEffect(() => {
    if (!unlocked || !currentData?.patientName) return
    const key = `${currentData.id}-${currentData.confirmedAllotedNumber}`
    if (spokenRef.current.cabin === key) return
    spokenRef.current.cabin = key
    // auto OFF hai -> "bol diya" maan lo, taaki baad me ON karne par purana naam repeat na ho
    if (!autoAnnounce) return
    const t = setTimeout(() => {
      speak(buildAnnouncement(currentData, 'cabin', { hindi: canHindi, ...ANNOUNCE_OPTS }))
    }, 1500)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentData?.id, currentData?.confirmedAllotedNumber, unlocked, autoAnnounce])

  // ===== announce: next patient =====
  // cabin wali announcement ke baad queue me bolti hai (4.5 sec gap).
  useEffect(() => {
    if (!unlocked || !nextPatientData?.patientName) return
    const key = `${nextPatientData.id}-${nextPatientData.confirmedAllotedNumber}`
    if (spokenRef.current.next === key) return
    spokenRef.current.next = key
    if (!autoAnnounce) return
    const t = setTimeout(() => {
      speak(buildAnnouncement(nextPatientData, 'next', { hindi: canHindi, ...ANNOUNCE_OPTS }))
    }, 4500)
    return () => clearTimeout(t)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nextPatientData?.id, nextPatientData?.confirmedAllotedNumber, unlocked, autoAnnounce])

  // doctor offline/rest par chalti announcement band
  useEffect(() => {
    if (doctorStatus === 1) return
    stop()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doctorStatus])

  const GetPatientInTheCabin = () => {
    setLoding(true)
    start()
    opdRegisteredCrud.GetPatientInTheCabin({
      DoctorId: loginInfo.doctorId,
      CenterId: centerid
    }).then(res => {
      let mdata = res.data
      if (mdata.result.succeeded) {
        setCurrentData(mdata.data)
      } else {
        setCurrentData()
      }
      setLoding(false)
      end()
    }).catch(errors => {
      setCurrentData()
      setLoding(false)
      end()
    })
  }

  const GetPatientWhoNext = () => {
    setLoding(true)
    start()
    try {
      opdRegisteredCrud.GetPatientWhoNext({
        DoctorId: loginInfo.doctorId,
        CenterId: centerid
      }).then(res => {
        let mdata = res.data
        if (mdata.result.succeeded) {
          setNextPatientData(mdata.data)
        } else {
          setNextPatientData()
        }
        setLoding(false)
        end()
      }).catch(errors => {
        setNextPatientData()
        setLoding(false)
        end()
      })
    }
    catch (error) {
      console.warn(error)
    }
  }

  const GetCenterDetails = () => {
    setLoding(true);
    start()
    opdCentersCrud.GetById(centerid)
      .then((res) => {
        let mdata = res.data;
        if (mdata.result.succeeded) {
          setDoctorStatus(mdata.data.doctorStatus)
          setCenterData(mdata.data);
        } else {
          setCenterData();
        }
        setLoding(false);
        end()
      })
      .catch((errors) => {
        setCenterData();
        setLoding(false);
        end()
      });
  }

  // doctor status pills (display only)
  const DOCTOR_STATES = [
    { id: 1, label: "In The Cabin", Icon: FaUserMd, color: "bg-green-500" },
    { id: 4, label: "On Rest", Icon: MdOutlineFreeBreakfast, color: "bg-orange-500" },
    { id: 0, label: "Offline", Icon: MdOutlinePowerSettingsNew, color: "bg-red-500" },
  ]

  // shared patient block (used for current + next)
  const PatientBody = ({ data }) => (
    <>
      <div className="text-3xl font-bold leading-tight sm:text-5xl lg:text-6xl">{data.patientName}</div>
      <div className="mt-3 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-xl sm:text-2xl">
        <span className="opacity-80">Number:</span>
        <span className="text-4xl font-extrabold sm:text-6xl lg:text-7xl">{data.confirmedAllotedNumber}</span>
        {data.confirmedAllotedNumber != data.opdNumberAlloted &&
          <span className="text-base font-normal opacity-70">/{data.opdNumberAlloted}</span>}
        <StatusContainer bg={ETypeBg(data.type)} text={GetETypeName(data.type)} />
      </div>
      <div className="mt-3 text-base opacity-80 sm:text-lg">
        Ref No. {data.id}{data.registeredIdForRef && "/" + data.registeredIdForRef}
      </div>
    </>
  )

  return (
    <Container>
      <div className="flex w-full flex-col px-2 sm:px-4">

        <SoundGate unlocked={unlocked} unlock={unlockAll} className="mb-2" />

        {/* ===== Header ===== */}
        <Card
          isBorder={true}
          isBack={true}
          bgColor="bg-[#E6F0FF]"
          borderColor="border-[#4682B4]"
          title={
            <div className="flex w-full flex-col items-center justify-between gap-1 sm:flex-row">
              <span className="flex items-center gap-1.5 font-semibold">
                <MdOutlineCalendarMonth className="text-lg text-[#4682B4]" />
                Today: {moment(new Date()).format("DD/MM/YYYY")}
              </span>
              <span className="flex items-center gap-1.5 font-mono text-lg font-bold tracking-wide sm:text-xl">
                <MdOutlineAccessTime className="text-[#4682B4]" />
                {moment(today).format("LTS")}
              </span>
            </div>
          }
        >
          {/* connection + voice language + doctor status (display only) */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:justify-between">

            <div className="flex flex-wrap items-center justify-center gap-2">
              <button
                onClick={reConnect}
                title={isConnected ? "Live" : "Disconnected · tap to reconnect"}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition active:scale-95 sm:text-sm ${isConnected
                  ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                  : "bg-red-50 text-red-600 hover:bg-red-100"}`}
              >
                {isConnected ? <PiPlugsConnectedFill className="text-base" /> : <VscDebugDisconnect className="text-base" />}
                <span className={`h-2 w-2 rounded-full ${isConnected ? "animate-pulse bg-emerald-500" : "bg-red-500"}`} />
                {isConnected ? "Live" : "Reconnect"}
              </button>

              {/* announcement language */}
              <div className="inline-flex overflow-hidden rounded-lg border border-[#4682B4]/40 bg-white shadow-sm">
                <span className="flex items-center px-2 text-gray-500">
                  <MdVolumeUp className="text-base text-[#4682B4]" />
                </span>
                {VOICE_OPTIONS.map(({ id, label }) => (
                  <button
                    key={id}
                    onClick={() => changeVoiceLang(id)}
                    title={`Announce patient names in ${label}`}
                    className={`px-3 py-2 text-xs font-semibold transition active:scale-95 sm:text-sm ${voiceLang === id
                      ? "bg-[#4682B4] text-white"
                      : "text-gray-600 hover:bg-gray-100"}`}
                  >
                    {label}
                  </button>
                ))}
              </div>

              {/* auto announcement on/off */}
              <button
                onClick={toggleAutoAnnounce}
                aria-pressed={autoAnnounce}
                title={autoAnnounce
                  ? "Auto announcement ON — patient badalte hi naam bolega (band karne ke liye tap karein)"
                  : "Auto announcement OFF — sirf manual Announce se bolega"}
                className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-xs font-semibold transition active:scale-95 sm:text-sm ${autoAnnounce
                  ? "bg-emerald-50 text-emerald-600 hover:bg-emerald-100"
                  : "bg-gray-100 text-gray-500 hover:bg-gray-200"}`}
              >
                {autoAnnounce ? <MdVolumeUp className="text-base" /> : <MdVolumeOff className="text-base" />}
                Auto {autoAnnounce ? "ON" : "OFF"}
              </button>

              {/* dobara pukaro */}
              {unlocked && currentData?.patientName &&
                <button
                  onClick={announceAgain}
                  title="Announce current patient again"
                  className="inline-flex items-center gap-1.5 rounded-lg bg-[#4682B4]/10 px-3 py-2 text-xs font-semibold text-[#2F5D85] transition hover:bg-[#4682B4]/20 active:scale-95 sm:text-sm"
                >
                  <MdCampaign className="text-base" />
                  Announce
                </button>
              }

              {/* hi-IN voice data missing -> English me bol raha hai */}
              {hindiMissing &&
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-700 sm:text-sm">
                  <MdWarningAmber className="text-base" />
                  Hindi voice not installed
                  {isNative &&
                    <button
                      onClick={openVoiceInstall}
                      className="ml-1 inline-flex items-center gap-1 rounded-md bg-amber-600 px-2 py-1 text-white transition active:scale-95"
                    >
                      <MdOutlineFileDownload className="text-sm" /> Install
                    </button>
                  }
                </span>
              }
            </div>

            <div className="inline-flex max-w-full overflow-x-auto rounded-lg shadow-sm">
              {DOCTOR_STATES.map(({ id, label, Icon, color }) => (
                <span
                  key={id}
                  className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-xs font-semibold text-white transition sm:px-4 sm:text-sm ${doctorStatus == id ? color : "bg-gray-400"}`}
                >
                  <Icon className="text-sm sm:text-base" />
                  {label}
                </span>
              ))}
            </div>
          </div>

          {/* center info */}
          {centerData &&
            <div className="mt-4 flex flex-col items-center text-center text-gray-800">
              <div className="text-3xl font-bold sm:text-5xl lg:text-6xl">{centerData.centerName}</div>
              <span className="mt-1 flex items-center gap-1.5 text-lg font-normal text-gray-600 sm:text-xl">
                <MdOutlineAccessTime className="shrink-0 text-[#4682B4]" />
                Opd Time: {moment(centerData.opdStartTime).format("LT")}-{moment(centerData.opdEndTime).format("LT")}
              </span>
              <div className="mt-1 flex items-center gap-1.5 text-xl text-gray-600 sm:text-3xl">
                <MdOutlineLocationOn className="shrink-0 text-[#4682B4]" />
                <span>{centerData.address}</span>
              </div>
            </div>
          }
        </Card>

        <div className="my-4" />

        {/* ===== Patients ===== */}
        <div className={`grid grid-cols-1 gap-4 ${nextPatientData ? "md:grid-cols-2" : ""}`}>

          {/* current */}
          <Card isBorder={true} bgColor="bg-amber-50" borderColor="border-[#FF8C00]">
            {loding ? (
              <LoadingContainer text="Please wait..." />
            ) : (
              <div className="py-6 text-center text-[#7A4C00]">
                {currentData ? (
                  <>
                    <div className="flex items-center justify-center gap-2 text-sm font-semibold uppercase tracking-wide opacity-70 sm:text-lg">
                      <MdOutlineMeetingRoom className="text-lg sm:text-2xl" /> Patient In The Cabin
                    </div>
                    <div className="mt-3"><PatientBody data={currentData} /></div>
                  </>
                ) : (
                  <div className="flex flex-col items-center gap-3 py-10">
                    <MdHourglassEmpty className="text-4xl opacity-60 sm:text-5xl" />
                    <div className="text-2xl font-semibold sm:text-3xl">OPD Not Started</div>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* next */}
          {nextPatientData &&
            <Card isBorder={true} bgColor="bg-brand-500" borderColor="border-green-500">
              {loding ? (
                <LoadingContainer text="Please wait..." />
              ) : (
                <div className="py-6 text-center text-white">
                  <div className="flex items-center justify-center gap-2 text-sm font-bold uppercase tracking-wide opacity-90 sm:text-lg">
                    <MdSkipNext className="text-lg sm:text-2xl" /> Next Patient
                  </div>
                  <div className="mt-3"><PatientBody data={nextPatientData} /></div>
                </div>
              )}
            </Card>
          }
        </div>
      </div>
    </Container>
  )
}